import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/app/admin/_common/ui/tabs";
import { Badge } from "@/app/admin/_common/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/app/admin/_common/ui/sheet";
import { tabTriggerClassName } from "@/app/admin/shows/[show_id]/_cmp/ShowDetailHeader";
import CuoButton from "@/app/admin/_common/CuoButton";
import PopOverLinkCopyButton from "@/app/admin/_common/PopOverLinkCopyButton";
import MerchService from "@/app/_services/MerchService";
import {
  MERCH_STATUS_BADGE_CLASSES,
  MERCH_STATUS_DOT_CLASSES,
  MERCH_STATUS_LABEL,
  toMerchStatus,
} from "@/app/admin/merch/_cmp/merchStatus";
import { ArrowLeft, Eye, EyeOff, MoreVertical, Store } from "lucide-react";
import { useSessionAuth } from "@/app/_utils/sessionClient";
import { PERMISSIONS } from "@/app/_utils/permissions";

const NAV_TABS = [
  { value: "details", label: "Details", permission: PERMISSIONS.PAGE_MERCH },
  { value: "orders", label: "Orders", permission: PERMISSIONS.PAGE_ORDERS },
  { value: "settings", label: "Settings", permission: PERMISSIONS.MERCH_WRITE },
];

export default function MerchDetailHeader({
  merch,
  productId,
}: {
  merch: any;
  productId?: string;
}) {
  const navigate = useNavigate();
  const { can } = useSessionAuth();
  const { pathname } = useLocation();
  const [isPublishing, setIsPublishing] = useState(false);

  const product = merch?.[0];
  const activeTab = NAV_TABS.find((t) => pathname.includes(`/${t.value}`))?.value ?? "details";
  const status = toMerchStatus(product?.status);
  const visibleTabs = NAV_TABS.filter((tab) => can(tab.permission));

  async function handleTogglePublish() {
    if (!product) return;
    setIsPublishing(true);
    try {
      await MerchService.publishMerchAsync(product.group_id, {
        published: !product.published,
      });
      window.location.reload();
    } finally {
      setIsPublishing(false);
    }
  }

  const titleContent = (
    <h1 className="my-0 flex min-w-0 items-center gap-2 truncate py-0.5 text-[21px] font-bold leading-none tracking-tight text-foreground">
      <Store className="size-5 flex-none text-foreground" />
      {product?.title ?? "Merch"}
    </h1>
  );

  const metaContent = (
    <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-muted-foreground">
      <Badge
        variant="outline"
        className={`gap-1.5 rounded-[7px] font-normal ${MERCH_STATUS_BADGE_CLASSES[status]}`}
      >
        <span className={`size-1.5 flex-none rounded-full ${MERCH_STATUS_DOT_CLASSES[status]}`} />
        {MERCH_STATUS_LABEL[status]}
      </Badge>
      <span>{product?.published ? "Published" : "Unpublished"}</span>
    </div>
  );

  const publishLabel = product?.published ? "Unpublish" : "Publish";
  const publishIcon = product?.published ? (
    <EyeOff className="size-3.5" />
  ) : (
    <Eye className="size-3.5" />
  );

  const mobileActionsSheet = (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Merch actions"
          className="inline-flex size-[30px] flex-none items-center justify-center rounded-lg border border-input bg-card text-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:hidden"
        >
          <MoreVertical className="size-4 shrink-0" size={16} />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-xl bg-white p-0 md:hidden">
        <div className="flex flex-col pb-6 pt-6">
          {product?.shopify_url && (
            <>
              <PopOverLinkCopyButton fullWidthRow title="Copy link" url={product.shopify_url} />
              <div className="mx-6 border-t border-border" />
            </>
          )}
          <CuoButton
            variant="ghost"
            label={publishLabel}
            icon={publishIcon}
            onClick={handleTogglePublish}
            disableOnAsync={isPublishing}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal"
          />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="flex flex-col gap-2.5 border-b border-border bg-card/60 px-5 pt-5 backdrop-blur-[2px]">
      <button
        type="button"
        onClick={() => navigate("/admin/merch")}
        className="inline-flex w-fit items-center gap-1 text-[12.5px] leading-none text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to Merch
      </button>

      {/* Mobile: title + actions trigger on one row, status on its own row below */}
      <div className="flex flex-col gap-2.5 md:hidden">
        <div className="flex items-center justify-between gap-3">
          {titleContent}
          {mobileActionsSheet}
        </div>
        {metaContent}
      </div>

      {/* Desktop: title + status grouped on the left, actions on the right */}
      <div className="hidden items-center justify-between gap-3 md:flex">
        <div className="flex min-w-0 items-center gap-3">
          {titleContent}
          {metaContent}
        </div>
        <div className="flex flex-none items-center gap-2">
          {product?.shopify_url && (
            <PopOverLinkCopyButton title="Link" url={product.shopify_url} />
          )}
          <CuoButton
            variant="outline"
            label={publishLabel}
            icon={publishIcon}
            onClick={handleTogglePublish}
            disableOnAsync={isPublishing}
          />
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          navigate(`/admin/merch/${productId ?? product?.group_id}/${v}`, { replace: true })
        }
        className="mt-0"
      >
        <TabsList className="h-auto w-full justify-start gap-5 overflow-x-auto border-0 bg-transparent p-0">
          {visibleTabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className={`flex-none ${tabTriggerClassName}`}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}
